// Реестр ведомостей по городам.
//
// 11.10.2026 финансовый директор заказчика: «хотелось бы сгруппировать ведомости по городам:
// Новый Уренгой список объектов, Пыть-Ях список объектов, Нижневартовск и так далее, с
// возможностью свернуть каждый город». Реестр шёл по названию объекта, и объекты одного города
// разъезжались по всему списку: «"БурСервис" г. Новый-Уренгой» в начале, «ООО "ТСК" г. Новый
// Уренгой» через тридцать строк, «г. Новый Уренгой Везерфорд» в самом конце.
//
// ## Откуда город
//
// Из карточки объекта (`site.city`): сервер отдаёт его в строке реестра. Из названия город НЕ
// вытаскивается: «г.Пермь», «г. Новый-уренгой», «пгт. Пойковский», «Шлюмберже - ВЧНГ-Иркутская
// область» — разбор такого однажды молча положит объект не в тот город. Город не указан —
// группа «Город не указан» последней; правится он в карточке объекта.
//
// ## Когда групп нет
//
// Город один или не указан ни у кого — список рисуется как раньше, без шапок: одна шапка над
// всеми строками ничего не группирует. Во время поиска тоже без групп: ищут конкретный объект,
// и шапки между тремя найденными строками только мешают.
//
// ## Что помнится
//
// Свёрнутые города — на устройстве, как тема и ширина колонки: это раскладка экрана, а не
// данные. В адресе ей не место: ссылка, присланная коллеге, обязана открыть ему ведомость, а не
// чужие свёрнутые города.

import type { PayrollOut } from '@/shared/api/endpoints/payroll';

import { ключПоиска } from './объекты-периода';

/** Ключ группы объектов без города. Ключи городов — буквы и пробелы, совпасть с ним не могут. */
export const БЕЗ_ГОРОДА = '—';

export interface ГородРеестра {
  /** Ключ для памяти «свёрнут»: без регистра, без разницы «е» и «ё», дефиса и пробела. */
  ключ: string;
  /** Как город записан в карточке объекта; у группы без города — «Город не указан». */
  название: string;
  ведомости: PayrollOut[];
}

export interface РеестрПоГородам {
  /** Ведомости аппарата управления: объекта у них нет, значит и города. Стоят первыми. */
  аппарат: PayrollOut[];
  /** Города по алфавиту, «Город не указан» последним. */
  города: ГородРеестра[];
}

/**
 * «Новый Уренгой», «новый-уренгой», «г. Новый  Уренгой» и «г.Новый Уренгой» — один город.
 *
 * Приставка «г.», «пгт.», «пос.» снимается: в названиях объектов заказчика город всегда с ней
 * («г.Пермь», «пгт. Пойковский»), и вписанный в карточку по привычке с приставкой он иначе
 * встал бы второй группой рядом с первой. Однобуквенная приставка снимается только с точкой или
 * пробелом после неё, поэтому «Пермь» и «Сургут» не задеваются.
 */
export function ключГорода(город: string): string {
  return ключПоиска(город)
    .replace(/^(?:г|гор|город|пгт|пос|поселок|п|с|д|н)(?:\.\s*|\s+)/u, '')
    .replace(/[\s-]+/g, ' ')
    .trim();
}

/**
 * Разложить строки реестра по городам.
 *
 * Порядок строк внутри города прежний — тот, что пришёл (с людьми сверху, пустые в конце):
 * группировка отвечает на вопрос «где», а не меняет того, что в каждом городе видно первым.
 */
export function поГородам(ведомости: readonly PayrollOut[]): РеестрПоГородам {
  const аппарат: PayrollOut[] = [];
  const безГорода: PayrollOut[] = [];
  const города = new Map<string, ГородРеестра>();
  for (const ведомость of ведомости) {
    if (ведомость.is_administrative) {
      аппарат.push(ведомость);
      continue;
    }
    const город = (ведомость.site_city ?? '').trim();
    if (город === '') {
      безГорода.push(ведомость);
      continue;
    }
    const ключ = ключГорода(город);
    const группа = города.get(ключ);
    if (группа === undefined) {
      города.set(ключ, { ключ, название: город, ведомости: [ведомость] });
    } else {
      группа.ведомости.push(ведомость);
    }
  }
  const поАлфавиту = [...города.values()].sort((а, б) =>
    а.название.localeCompare(б.название, 'ru'),
  );
  return {
    аппарат,
    города:
      безГорода.length === 0
        ? поАлфавиту
        : [...поАлфавиту, { ключ: БЕЗ_ГОРОДА, название: 'Город не указан', ведомости: безГорода }],
  };
}

/** Есть что группировать: объекты хотя бы из двух разных мест, считая «город не указан». */
export function естьЧтоГруппировать(реестр: РеестрПоГородам): boolean {
  return реестр.города.length >= 2;
}

const ПАМЯТЬ = 'bark.sheets.collapsed-cities';

/** Какие города человек свернул на этом устройстве. */
export function свёрнутыеГорода(): Set<string> {
  try {
    const сырое = localStorage.getItem(ПАМЯТЬ);
    const разобрано: unknown = сырое === null ? [] : JSON.parse(сырое);
    return new Set(
      Array.isArray(разобрано)
        ? разобрано.filter((ключ): ключ is string => typeof ключ === 'string')
        : [],
    );
  } catch {
    // Хранилище недоступно или в нём мусор: всё развёрнуто, ничего не спрятано.
    return new Set();
  }
}

export function запомнитьСвёрнутые(ключи: ReadonlySet<string>): void {
  try {
    if (ключи.size === 0) {
      localStorage.removeItem(ПАМЯТЬ);
    } else {
      localStorage.setItem(ПАМЯТЬ, JSON.stringify([...ключи]));
    }
  } catch {
    // Не запомнили: в этом окне города свёрнуты, после перезапуска развернутся.
  }
}
